import { ref, computed, watch } from 'vue';
import { searchGlobal, type SearchResponse, type SearchResults } from '@/api/search';
import type { CollabArtifactType } from '@/api/collab';

export interface GlobalArtifactHit {
  type: CollabArtifactType;
  id: string;
  label: string;
  sublabel: string;
}

/** Flatten `GET /api/search` buckets into a single pick list (quick-add, future palette). */
export function flattenSearchResults(results: SearchResults | null): GlobalArtifactHit[] {
  if (!results) return [];
  const hits: GlobalArtifactHit[] = [];
  for (const a of results.actors) {
    hits.push({ type: 'actor', id: String(a.id), label: a.name, sublabel: `actor · ${a.type}` });
  }
  for (const c of results.campaigns) {
    hits.push({ type: 'campaign', id: String(c.id), label: c.name, sublabel: `campaign · ${c.status}` });
  }
  for (const i of results.indicators) {
    hits.push({ type: 'indicator', id: String(i.id), label: i.value, sublabel: `indicator · ${i.type}` });
  }
  for (const r of results.rules) {
    hits.push({ type: 'rule', id: String(r.id), label: r.name, sublabel: `rule · ${r.severity}` });
  }
  for (const t of results.techniques) {
    hits.push({ type: 'technique', id: t.id, label: `${t.id} — ${t.name}`, sublabel: 'technique' });
  }
  for (const r of results.reports) {
    hits.push({ type: 'report', id: String(r.id), label: r.title, sublabel: `report · ${r.status}` });
  }
  return hits;
}

export interface UseGlobalArtifactSearchOptions {
  debounceMs?: number;
  minQueryLength?: number;
}

/**
 * Debounced global artifact search (same pipeline as Quick Add note).
 * SSOT for search state + flattening; callers own selection / submit.
 */
export function useGlobalArtifactSearch(options?: UseGlobalArtifactSearchOptions) {
  const debounceMs = options?.debounceMs ?? 200;
  const minLen = options?.minQueryLength ?? 2;

  const query = ref('');
  const searching = ref(false);
  const searchError = ref<string | null>(null);
  const results = ref<SearchResponse['results'] | null>(null);
  let searchAbort: AbortController | null = null;
  let searchTimeout: ReturnType<typeof setTimeout> | null = null;

  const flatResults = computed(() => flattenSearchResults(results.value));

  function runSearch() {
    if (searchTimeout) clearTimeout(searchTimeout);
    if (searchAbort) searchAbort.abort();
    if (query.value.trim().length < minLen) {
      results.value = null;
      searching.value = false;
      return;
    }
    searchTimeout = setTimeout(async () => {
      searching.value = true;
      searchError.value = null;
      searchAbort = new AbortController();
      try {
        const res = await searchGlobal(query.value.trim(), searchAbort.signal);
        results.value = res.results;
      } catch (err: unknown) {
        if ((err as { code?: string })?.code !== 'ERR_CANCELED') {
          searchError.value = 'Search failed';
        }
      } finally {
        searching.value = false;
      }
    }, debounceMs);
  }

  watch(query, runSearch);

  return {
    query,
    searching,
    searchError,
    results,
    flatResults,
    runSearch,
  };
}
